import React, { useContext, useEffect, useRef, useState } from "react";
import * as QuickSightEmbedding from "amazon-quicksight-embedding-sdk";
import { Alert, Box, Link, Spinner } from "@amzn/awsui-components-react";
import { useQuery } from "@tanstack/react-query";
import { DashboardApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import DarkModeContext from "../../contexes/DarkModeContext";
import {
  compressAndSerializeData,
  decompressAndDeserializeData,
} from "../../helpers/compressionUtils";
import { QsFilterGroup, RULE_PARAM, planRuleFilters } from "./ruleFilters";
import { RowActionRegistrar, registerInvestigateAction } from "./rowActions";
import {
  InvestigationPredicate,
  Stage,
} from "../../lusca-search-api/generated-src";
import { PERMISSION_LINK } from "../../constants";

export type DashboardType =
  | "dlq"
  | "testing"
  | "pv_channels"
  | "pv_addon"
  | "pv_tvod"
  | "royalty_recon_tvod"
  | "royalty_recon_channels";

interface QuickSightDashboardProps {
  stage: Stage;
  dashboardType: DashboardType;
  onLoad?: () => void;
  /** Invoked for QuickSight custom callback actions (row actions "5. Show in Investigations Pane" / "6. Go to Investigation (new tab)"). */
  onCallback?: (message: unknown) => void;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams) => void;
  setShareHandler: React.Dispatch<
    React.SetStateAction<(() => Promise<string>) | null>
  >;
  setFiltersHandler?: React.Dispatch<
    React.SetStateAction<(() => Promise<string>) | null>
  >;
  filterRequest?: {
    filters: string | null;
    ts: number;
  };
  activeViewName?: string | null;
}

const CONTAINER_HEIGHT = "calc(100vh - 60px)";
/** Debounce for re-checking the DLQ row action after bursty dashboard events (see below). */
export const ROW_ACTION_RECHECK_DELAY_MS = 750;
/**
 * QuickSight emits no filter-changed embedding event. Polling is therefore the only way for the
 * host to notice that the dashboard's "Investigated?" filter now guarantees every visible row is
 * eligible for action 6. Split visuals do not depend on this poll, but use the same reconciliation.
 */
export const ROW_ACTION_FILTER_POLL_MS = 1500;

export const QuickSightDashboard: React.FC<QuickSightDashboardProps> = ({
  stage,
  dashboardType,
  onLoad,
  onCallback,
  searchParams,
  setSearchParams,
  setShareHandler,
  setFiltersHandler,
  filterRequest,
  activeViewName,
}) => {
  const containerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const dashboardExperienceRefs = useRef<{
    [key: string]: QuickSightEmbedding.DashboardExperience | null;
  }>({});
  const embedAttemptedRefs = useRef<{ [key: string]: boolean }>({});
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const darkModeContext = useContext(DarkModeContext);
  const [isDarkTheme, setIsDarkTheme] = useState(darkModeContext);
  // Keep the latest callback without re-embedding the dashboard when the parent re-renders.
  const onCallbackRef = useRef(onCallback);
  onCallbackRef.current = onCallback;
  // Sheets (per embedded dashboard) with a row-action registration in flight. This
  // is only a lock, not a "done" marker: the toolbar "Reset to original dashboard" button wipes
  // runtime-added visual actions, so every trigger re-checks the live visual actions instead.
  const rowActionInFlightRef = useRef<{ [key: string]: Set<string> }>({});
  const rowActionTimerRef = useRef<{
    [key: string]: ReturnType<typeof setTimeout> | undefined;
  }>({});
  const rowActionPollRef = useRef<{
    [key: string]: ReturnType<typeof setInterval> | undefined;
  }>({});

  // DLQ supports switching between Prod/Gamma; all other dashboards are single-stage.
  const isMultiStage = dashboardType === "dlq";
  const activeKey = isMultiStage ? stage : dashboardType;

  /**
   * Adds the right-click "5. Show in Investigations Pane" and "6. Go to Investigation (new tab)" actions to the DLQ row visual(s) of a sheet at
   * runtime, so no per-dashboard QuickSight edit is needed. `registerInvestigateAction` checks the
   * visual's live actions first, so calling this repeatedly is safe and re-adds the action after
   * the user hits "Reset to original dashboard" (which discards runtime actions). Failures are
   * logged and never surface to the user because the toolbar "Investigate DLQ item" still works.
   */
  const ensureRowAction = async (
    experience: RowActionRegistrar,
    key: string,
    sheetId?: string
  ) => {
    if (dashboardType !== "dlq") return;
    if (!rowActionInFlightRef.current[key]) {
      rowActionInFlightRef.current[key] = new Set<string>();
    }
    const inFlight = rowActionInFlightRef.current[key];
    let target: string | undefined;
    try {
      target = sheetId ?? (await experience.getSelectedSheetId());
      // A second CONTENT_LOADED / sheet event while the first is still registering would add the
      // action twice (the live check runs before addVisualActions resolves).
      if (inFlight.has(target)) return;
      inFlight.add(target);
      const result = await registerInvestigateAction(experience, target);
      if (
        result.registered.length ||
        result.removed.length ||
        result.failed.length
      ) {
        console.info("DLQ row action registration:", result);
      }
      if (!result.matchedByName && result.registered.length) {
        console.warn(
          "No visual title matched the DLQ row table; the row action was added to every visual on the sheet. " +
            "Add the table's title to ROW_VISUAL_PATTERNS in rowActions.ts.",
          result.registered
        );
      }
    } catch (err) {
      console.error("Failed to register the DLQ row action:", err);
    } finally {
      if (target) inFlight.delete(target);
    }
  };

  /**
   * Debounced re-check used for events that fire in bursts (PARAMETERS_CHANGED, SIZE_CHANGED).
   * "Reset to original dashboard" is a toolbar button inside the iframe, so the host page gets no
   * dedicated event for it — but it resets the parameters and re-lays out the sheet, which is
   * enough to schedule a check; the live-action check makes a no-op cheap.
   */
  const scheduleRowActionCheck = (
    experience: RowActionRegistrar | null,
    key: string
  ) => {
    if (dashboardType !== "dlq" || !experience) return;
    const pending = rowActionTimerRef.current[key];
    if (pending) clearTimeout(pending);
    rowActionTimerRef.current[key] = setTimeout(() => {
      rowActionTimerRef.current[key] = undefined;
      ensureRowAction(experience, key).catch((err) =>
        console.error("Row action registration failed:", err)
      );
    }, ROW_ACTION_RECHECK_DELAY_MS);
  };

  const startRowActionFilterPolling = (
    experience: RowActionRegistrar | null,
    key: string
  ) => {
    if (
      dashboardType !== "dlq" ||
      !experience ||
      rowActionPollRef.current[key]
    ) {
      return;
    }
    rowActionPollRef.current[key] = setInterval(() => {
      ensureRowAction(experience, key).catch((err) =>
        console.error("Row action filter poll failed:", err)
      );
    }, ROW_ACTION_FILTER_POLL_MS);
  };

  // Drop any pending row-action re-check/filter poll when the dashboard unmounts.
  useEffect(
    () => () => {
      Object.values(rowActionTimerRef.current).forEach((t) => {
        if (t) clearTimeout(t);
      });
      Object.values(rowActionPollRef.current).forEach((t) => {
        if (t) clearInterval(t);
      });
      rowActionTimerRef.current = {};
      rowActionPollRef.current = {};
    },
    []
  );

  // Persist a container per key so switching stages doesn't force a re-embed.
  useEffect(() => {
    if (mainContainerRef.current) {
      const containerKeys = isMultiStage
        ? [Stage.Prod, Stage.Gamma]
        : [dashboardType];
      containerKeys.forEach((key) => {
        if (!containerRefs.current[key]) {
          const div = document.createElement("div");
          div.style.width = "100%";
          div.style.height = CONTAINER_HEIGHT;
          div.style.position = "absolute";
          div.style.top = "0";
          div.style.left = "0";
          div.style.display = "none";
          containerRefs.current[key] = div;
          mainContainerRef.current!.appendChild(div);
        }
      });
    }
  }, [dashboardType]);

  useEffect(() => {
    Object.keys(containerRefs.current).forEach((key) => {
      const container = containerRefs.current[key];
      if (container) {
        container.style.display = key === activeKey ? "block" : "none";
      }
    });
  }, [activeKey]);

  const dashboardApi = DashboardApiFactory();

  const THEMES = {
    DARK: isMultiStage
      ? stage === Stage.Gamma
        ? "arn:aws:quicksight:us-east-1:239039480348:theme/cloudscape"
        : "arn:aws:quicksight:us-east-1:800989574994:theme/e42b2ecd-7436-45a0-a032-52469c99af64"
      : "arn:aws:quicksight::aws:theme/MIDNIGHT",
    LIGHT: "arn:aws:quicksight::aws:theme/CLASSIC",
  };

  const applyTheme = async (newTheme: boolean) => {
    const currentDashboard = dashboardExperienceRefs.current[activeKey];
    if (currentDashboard) {
      try {
        const setThemePromise = new Promise((resolve, reject) => {
          try {
            const result = currentDashboard.setTheme(
              newTheme ? THEMES.DARK : THEMES.LIGHT
            );
            resolve(result);
          } catch (e) {
            reject(e);
          }
        });

        await Promise.race([
          setThemePromise,
          new Promise((_, reject) => {
            setTimeout(
              () => reject(new Error("Theme change timed out")),
              30000
            );
          }),
        ]);

        setIsDarkTheme(newTheme);
      } catch (err) {
        console.warn(
          "Theme change timed out (this is expected and harmless - dashboard continues to work):",
          err
        );
      }
    } else {
      setIsDarkTheme(newTheme);
    }
  };

  useEffect(() => {
    const currentDashboard = dashboardExperienceRefs.current[activeKey];
    if (currentDashboard) {
      applyTheme(darkModeContext);
    }
  }, [darkModeContext, isLoading]);

  const { data: embedUrlData, error: embedUrlError } = useQuery(
    ["dashboardEmbed", stage, dashboardType],
    async () => {
      const { data } = await dashboardApi.getDashboardEmbedUrl(
        dashboardType,
        stage
      );
      if (!data?.embedUrl) {
        throw new Error(
          (data as { message?: string })?.message ||
            (data as { error?: string })?.error ||
            "Failed to load dashboard"
        );
      }
      return data;
    },
    {
      enabled: !!stage,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
    }
  );

  useEffect(() => {
    if (embedUrlError) {
      setError(
        embedUrlError instanceof Error
          ? embedUrlError
          : new Error("Failed to load dashboard")
      );
      setIsLoading(false);
    }
  }, [embedUrlError]);

  const getFilters = async () => {
    const experience = dashboardExperienceRefs.current[activeKey];
    if (!experience) throw new Error("Dashboard not loaded");

    const sheetId = await experience.getSelectedSheetId();
    return experience.getFilterGroupsForSheet(sheetId);
  };

  const restoreFilters = async (filtersParams: string) => {
    const experience = dashboardExperienceRefs.current[activeKey];
    if (!experience) throw new Error("Dashboard not loaded");

    const parsedFilterGroups =
      decompressAndDeserializeData<any[]>(filtersParams);
    const existingFilterGroups = await getFilters();

    // updateFilterGroups will silently ignore the whole request if a request contains
    // any FilterGroupId that doesn't already exist.
    const changedFilterGroups = parsedFilterGroups.filter((newFg) => {
      const existing = existingFilterGroups.find(
        (e) => e.FilterGroupId === newFg.FilterGroupId
      );
      return existing && JSON.stringify(existing) !== JSON.stringify(newFg);
    });
    const addedFilterGroups = parsedFilterGroups.filter(
      (newFg) =>
        !existingFilterGroups.some(
          (e) => e.FilterGroupId === newFg.FilterGroupId
        )
    );

    if (changedFilterGroups.length !== 0) {
      await experience.updateFilterGroups(changedFilterGroups);
    }
    if (addedFilterGroups.length !== 0) {
      await experience.addFilterGroups(addedFilterGroups);
    }
  };

  /**
   * `?rule=` (from an investigation's "View in dashboard") narrows the dashboard to the
   * investigation's population by driving the dashboard's own category filters.
   */
  const applyRuleParam = async (ruleParam: string) => {
    const experience = dashboardExperienceRefs.current[activeKey];
    if (!experience) throw new Error("Dashboard not loaded");
    const rule =
      decompressAndDeserializeData<Array<InvestigationPredicate>>(ruleParam);
    const existing = await getFilters();
    const plan = planRuleFilters(existing as QsFilterGroup[], rule);
    if (plan.updates.length) await experience.updateFilterGroups(plan.updates);
    if (plan.unmatchedFields.length) {
      console.warn(
        "Dashboard has no filter for rule field(s); population view is wider than the rule:",
        plan.unmatchedFields
      );
    }
  };

  useEffect(() => {
    if (dashboardExperienceRefs.current[activeKey]) {
      setIsLoading(false);
      return;
    }

    if (
      embedUrlData?.embedUrl &&
      containerRefs.current[activeKey] &&
      !embedAttemptedRefs.current[activeKey]
    ) {
      embedAttemptedRefs.current[activeKey] = true;
      const embedKey = activeKey;
      const embedDashboard = async () => {
        // The experience handle is only available once embedDashboard resolves, but CONTENT_LOADED
        // may arrive before that; remember the request and honour it after assignment.
        let experienceHandle: QuickSightEmbedding.DashboardExperience | null =
          null;
        let rowActionPending = false;
        try {
          setIsLoading(true);
          const embeddingContext =
            await QuickSightEmbedding.createEmbeddingContext();
          const experience = await embeddingContext.embedDashboard(
            {
              url: embedUrlData.embedUrl,
              container: containerRefs.current[activeKey]!,
              width: "100%",
              height: "100%",
              resizeHeightOnSizeChangedEvent: true,
              onChange: (event: QuickSightEmbedding.EmbeddingEvents) => {
                if (event.eventName === "FRAME_LOADED") {
                  setIsLoading(false);
                  onLoad?.();
                  const filtersParam = searchParams.get("filters");
                  const ruleParam = searchParams.get(RULE_PARAM);
                  if (filtersParam) {
                    restoreFilters(filtersParam).catch((err) =>
                      console.error("Failed to restore filters:", err)
                    );
                  } else if (ruleParam) {
                    applyRuleParam(ruleParam).catch((err) =>
                      console.error("Failed to apply investigation rule:", err)
                    );
                  }
                }
              },
            },
            {
              locale: "en-US",
              sheetOptions: {
                initialSheetId: undefined,
                singleSheet: false,
                emitSizeChangedEventOnSheetChange: true,
              },
              toolbarOptions: {
                export: true,
                undoRedo: true,
                reset: true,
                bookmarks: true,
              },
              themeOptions: {
                themeArn: isDarkTheme ? THEMES.DARK : THEMES.LIGHT,
              },
              attributionOptions: {
                overlayContent: false,
              },
              onMessage: async (messageEvent: {
                eventName?: string;
                message?: unknown;
              }) => {
                // Custom "callback" actions on the DLQ row visual arrive here; the payload carries the
                // datapoint columns (dlq_pk + seed fields). Lusca always re-checks the live registry.
                if (messageEvent?.eventName === "CALLBACK_OPERATION_INVOKED") {
                  onCallbackRef.current?.(messageEvent.message);
                  return;
                }
                // Register the row action once the sheet's visuals exist, and again for every
                // sheet the user switches to.
                if (messageEvent?.eventName === "CONTENT_LOADED") {
                  if (experienceHandle) {
                    startRowActionFilterPolling(
                      experienceHandle as unknown as RowActionRegistrar,
                      embedKey
                    );
                    ensureRowAction(
                      experienceHandle as unknown as RowActionRegistrar,
                      embedKey
                    ).catch((err) =>
                      console.error("Row action registration failed:", err)
                    );
                  } else {
                    rowActionPending = true;
                  }
                  return;
                }
                if (messageEvent?.eventName === "SELECTED_SHEET_CHANGED") {
                  const sheetId = (
                    messageEvent.message as {
                      selectedSheet?: { SheetId?: string };
                    }
                  )?.selectedSheet?.SheetId;
                  const handle =
                    experienceHandle ??
                    dashboardExperienceRefs.current[embedKey];
                  if (handle) {
                    ensureRowAction(
                      handle as unknown as RowActionRegistrar,
                      embedKey,
                      sheetId
                    ).catch((err) =>
                      console.error("Row action registration failed:", err)
                    );
                  }
                  return;
                }
                // "Reset to original dashboard" (toolbar, inside the iframe) drops runtime visual
                // actions without a dedicated event; it does reset parameters and re-lay out the
                // sheet, so re-check on those (debounced, live check makes a no-op cheap).
                if (
                  messageEvent?.eventName === "PARAMETERS_CHANGED" ||
                  messageEvent?.eventName === "SIZE_CHANGED"
                ) {
                  scheduleRowActionCheck(
                    (experienceHandle ??
                      dashboardExperienceRefs.current[
                        embedKey
                      ]) as unknown as RowActionRegistrar | null,
                    embedKey
                  );
                }
              },
            }
          );
          experienceHandle = experience;
          dashboardExperienceRefs.current[embedKey] = experience;
          if (rowActionPending) {
            startRowActionFilterPolling(
              experience as unknown as RowActionRegistrar,
              embedKey
            );
            ensureRowAction(
              experience as unknown as RowActionRegistrar,
              embedKey
            ).catch((err) =>
              console.error("Row action registration failed:", err)
            );
          }
        } catch (err) {
          console.error("Error embedding dashboard for key:", embedKey, err);
          setError(
            err instanceof Error ? err : new Error("Failed to embed dashboard")
          );
          setIsLoading(false);
        }
      };

      embedDashboard();
    }
  }, [embedUrlData, stage, onLoad, isDarkTheme]);

  const formatQuickSightError = (err: unknown): string => {
    if (err instanceof Error) {
      try {
        const errorData = JSON.parse(
          err.message.replace("QuickSight Error: ", "")
        );
        return errorData.message?.[0] || err.message;
      } catch {
        return err.message.replace("QuickSight Error: ", "");
      }
    }
    return String(err);
  };

  const getAndSetUrlWithFilters = async () => {
    const filters = await getFilters();
    const newParams = new URLSearchParams(searchParams);
    newParams.set("filters", compressAndSerializeData(filters));
    if (activeViewName) {
      newParams.set("view", activeViewName);
    } else {
      newParams.delete("view");
    }
    setSearchParams(newParams);
    return window.location.href;
  };

  const getCompressedFilters = async () => {
    const filters = await getFilters();
    return compressAndSerializeData(filters);
  };

  useEffect(() => {
    setShareHandler(isLoading ? null : () => getAndSetUrlWithFilters);
    setFiltersHandler?.(isLoading ? null : () => getCompressedFilters);
  }, [isLoading, activeViewName, searchParams]);

  useEffect(() => {
    if (!filterRequest || isLoading) return;
    const experience = dashboardExperienceRefs.current[activeKey];
    if (!experience) return;

    if (filterRequest.filters === null) {
      experience
        .reset()
        .catch((err) => console.error("Failed to reset dashboard:", err));
    } else {
      restoreFilters(filterRequest.filters).catch((err) =>
        console.error("Failed to restore filters:", err)
      );
    }
  }, [filterRequest, isLoading]);

  if (error) {
    const isPermissionError = (error as any)?.response?.status === 403;
    return (
      <Box padding="l">
        <Alert
          type="error"
          header={
            isPermissionError ? "Permission Error" : "Error loading dashboard"
          }
          dismissible={false}
        >
          {isPermissionError ? (
            <>
              You don't have permission to view this dashboard. Ask your manager
              to add you to the{" "}
              <Link href={PERMISSION_LINK} external>
                Lusca users group
              </Link>
              .
            </>
          ) : (
            formatQuickSightError(error)
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box padding="n">
      {isLoading && (
        <Box margin="xxl" textAlign="center">
          <Spinner size="large" />
        </Box>
      )}
      <div
        ref={mainContainerRef}
        style={{
          width: "100%",
          height: CONTAINER_HEIGHT,
          position: "relative",
          visibility: isLoading ? "hidden" : "visible",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
    </Box>
  );
};

export default QuickSightDashboard;
